import { api, brandApi } from "../lib/axios";

export interface Plan {
  id: string;
  name: string;
  price: number;
  isActive: boolean;
  stripeProductId: string;
  stripePriceId: string;
  createdAt: string;
  updatedAt: string;
}

export interface Subscription {
  name: string;
  data: {
    id: string;
    active: boolean;
    planId: string;
    createdAt: string;
    updatedAt: string;
    sessionId: string;
    subscriptionId: string;
    plan: Plan;
  };
}

class PlanService {
  private static instance: PlanService;

  private constructor() {}

  public static getInstance(): PlanService {
    if (!PlanService.instance) {
      PlanService.instance = new PlanService();
    }
    return PlanService.instance;
  }

  async getCurrentSubscription(): Promise<Subscription | null> {
    try {
      const { data } = await brandApi.get('/subscription/current-subscription');
      return data;
    } catch (error) {
      console.error('Error fetching current subscription:', error);
      return null;
    }
  }

  async subscribeToPlan(planName: string): Promise<string> {
    const { data } = await brandApi.post('/subscription/create', { planName });
    return data.checkoutUrl;
  }

  async cancelSubscription(): Promise<void> {
    await brandApi.post('/subscription/cancel-subscription');
  }

  async getAdminInviteStatus(): Promise<{ invitedByAdmin: boolean }> {
    try {
      const { data } = await brandApi.get('/brand-admin-invitation/status');
      return data;
    } catch {
      return { invitedByAdmin: false };
    }
  }

  async attachAdminInviteToken(token: string): Promise<{ success: boolean; alreadyLinked?: boolean }> {
    const tempToken = localStorage.getItem("brand_temp_token");
    const { data } = await api.post(
      "/brand-admin-invitation/attach-session",
      { token },
      tempToken
        ? {
            headers: {
              Authorization: `Bearer ${tempToken}`,
            },
          }
        : undefined
    );
    return data;
  }

  async getGraceStatus(): Promise<{ hasThirtyDayGrace: boolean; reason: 'admin_invite' | 'landing' | 'otp_trial' | null; endsAt: string | null }> {
    try {
      const { data } = await brandApi.get('/subscription/grace-status');
      return data;
    } catch {
      return { hasThirtyDayGrace: false, reason: null, endsAt: null };
    }
  }

  async activateTrialWithOTP(code: string): Promise<{ success: boolean; trialDays: number; message: string }> {
    const { data } = await brandApi.post('/trial-otp/activate', { code });
    return data;
  }

  async getOTPTrialStatus(): Promise<{ hasOTPTrial: boolean; trialDays: number | null; activatedAt: string | null; endsAt: string | null }> {
    try {
      const { data } = await brandApi.get('/trial-otp/status');
      return data;
    } catch {
      return { hasOTPTrial: false, trialDays: null, activatedAt: null, endsAt: null };
    }
  }
}

export const planService = PlanService.getInstance();
